import React, { ReactNode, useState } from 'react';
import { LinkAsButton } from '@printdeal/ui-library';
import { LineItem } from '@printdeal/commercetools-v2/types';
import { hasSplitShipment } from '@printdeal/business-logic';
import { getLineItemQuantity } from '@printdeal/checkout-shared';
import { ParsedShippingAddressPerLineItem } from '@printdeal/checkout-frontend';
import { FormattedMessage } from 'react-intl';
import OrderAddressContent from './OrderAddressContent';
import ShippingAddressModal from './ShippingAddressModal';
import { SetShippingAddressResponse } from './queries/useSetShippingAddressMutation';

interface Props {
  orderId: string;
  lineItem: LineItem;
  /**
  * If multiple line items are grouped together, they can be updated together. Make sure to send all line items,
  * including the main line item in this field
  */
  groupedLineItems: LineItem[];
  shippingAddress: ParsedShippingAddressPerLineItem;
  canChangeShippingAddress: boolean;
  onChangeSuccess: (data: SetShippingAddressResponse) => void;
  children: ({ shippingAddress, changeShippingAddressButton }: {
    shippingAddress: ReactNode,
    changeShippingAddressButton: ReactNode,
  }) => ReactNode;
}

const ShippingAddressOnOrderLineItem = ({
  orderId,
  lineItem,
  groupedLineItems,
  shippingAddress,
  canChangeShippingAddress,
  onChangeSuccess,
  children,
}: Props) => {
  const hasSplitShipmentOnLineItem = hasSplitShipment(lineItem);
  // @ts-expect-error type mismatch
  const totalQuantity = hasSplitShipmentOnLineItem ? shippingAddress?.quantity : getLineItemQuantity(lineItem);
  const lineItems = groupedLineItems || [lineItem];

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      {children({
        shippingAddress: (
          <OrderAddressContent
            address={shippingAddress}
            quantity={Number(totalQuantity)}
          />
        ),
        changeShippingAddressButton: canChangeShippingAddress ? (
          <>
            <LinkAsButton
              variant="secondary"
              iconBefore="edit"
              className="pd-text-xs"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              <FormattedMessage id="address.button.changeShippingAddress" defaultMessage="Change shipping address" />
            </LinkAsButton>

            <ShippingAddressModal
              orderId={orderId}
              lineItemIds={lineItems.map(({ id }) => id)}
              shippingAddress={shippingAddress}
              isOpen={isModalOpen}
              onChangeSuccess={(data) => {
                onChangeSuccess(data);
                setIsModalOpen(false);
              }}
              onClose={() => setIsModalOpen(false)}
            />
          </>
        ) : null,
      })}
    </>
  );
};

export default ShippingAddressOnOrderLineItem;
